import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { WorkerTerminalError } from '../types/worker'
import type { ShopStockCountReportLine } from '../components/shop-stock-count/shop-stock-count-report'

export type ShopStockCountStatus = 'open' | 'submitted' | 'cancelled'
export type ShopStockCountMode = 'one_by_one' | 'barcode' | 'uhf'

export interface ShopStockCountRecord {
	count_id: string
	count_number: string
	status: ShopStockCountStatus
	mode: ShopStockCountMode
	location_id: string | null
	location_name: string | null
	started_at: string
	started_by_name: string | null
	submitted_at: string | null
	submitted_by_name: string | null
	notes: string | null
	report_email_to: string | null
	report_email_sent_at: string | null
	report_email_error: string | null
	report_printed_at: string | null
	line_count: number
	unit_count: number
	lines: ShopStockCountReportLine[]
}

interface UseShopStockCountsConfig {
	adminId: string | null
	status?: ShopStockCountStatus | null
	limit?: number
}

interface UseShopStockCountsReturn {
	counts: ShopStockCountRecord[]
	openCount: ShopStockCountRecord | null
	loading: boolean
	error: WorkerTerminalError | null
	load: () => Promise<void>
	refresh: () => Promise<void>
	clearError: () => void
}

function parseError(err: unknown): WorkerTerminalError {
	if (err instanceof Error) {
		const message = err.message.toLowerCase()
		if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
			return {
				type: 'connection',
				message: 'Unable to connect. Please check your internet connection.',
				retryable: true,
			}
		}
		if (message.includes('auth') || message.includes('jwt') || message.includes('token')) {
			return { type: 'auth', message: 'Your session has expired. Please log in again.', retryable: false }
		}
	}
	return { type: 'data', message: 'Unable to load stock counts. Please try again.', retryable: true }
}

function mapRow(row: Record<string, unknown>): ShopStockCountRecord {
	return {
		count_id: row.count_id as string,
		count_number: row.count_number as string,
		status: row.status as ShopStockCountStatus,
		mode: row.mode as ShopStockCountMode,
		location_id: (row.location_id as string) ?? null,
		location_name: (row.location_name as string) ?? null,
		started_at: row.started_at as string,
		started_by_name: (row.started_by_name as string) ?? null,
		submitted_at: (row.submitted_at as string) ?? null,
		submitted_by_name: (row.submitted_by_name as string) ?? null,
		notes: (row.notes as string) ?? null,
		report_email_to: (row.report_email_to as string) ?? null,
		report_email_sent_at: (row.report_email_sent_at as string) ?? null,
		report_email_error: (row.report_email_error as string) ?? null,
		report_printed_at: (row.report_printed_at as string) ?? null,
		line_count: Number(row.line_count) || 0,
		unit_count: Number(row.unit_count) || 0,
		lines: Array.isArray(row.lines) ? (row.lines as ShopStockCountReportLine[]) : [],
	}
}

/** Lists shop stock counts for the page history and the Reports tab. */
export function useShopStockCounts({
	adminId,
	status = null,
	limit = 200,
}: UseShopStockCountsConfig): UseShopStockCountsReturn {
	const [counts, setCounts] = useState<ShopStockCountRecord[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<WorkerTerminalError | null>(null)

	const load = useCallback(async () => {
		if (!adminId) return

		setLoading(true)
		setError(null)

		try {
			const { data, error: rpcError } = await supabase.rpc('get_shop_stock_counts', {
				p_admin_id: adminId,
				p_status: status,
				p_from: null,
				p_to: null,
				p_limit: limit,
			})

			if (rpcError) throw rpcError
			setCounts(((data ?? []) as Record<string, unknown>[]).map(mapRow))
		} catch (err) {
			console.error('Failed to load shop stock counts:', err)
			setError(parseError(err))
		} finally {
			setLoading(false)
		}
	}, [adminId, status, limit])

	const refresh = useCallback(async () => {
		await load()
	}, [load])

	const clearError = useCallback(() => setError(null), [])

	return {
		counts,
		openCount: counts.find((c) => c.status === 'open') ?? null,
		loading,
		error,
		load,
		refresh,
		clearError,
	}
}
